import React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import findResult from '@/utils/resultsHelper';
import AuditAction from '@/audit//components/AuditAction';
import { readStates, states } from '@/constants/lifecycle';
import { cohortUrl } from '@/constants/urls';
import DeleteActionMenu from '@/components/ribbon-actions/DeleteActionMenu';
import EditAction from '@/components/ribbon-actions/EditAction';
import { confirmCohortDeletion, editCohort } from '../actions';
import CancelCohortAction from './CancelCohortAction';
import SaveCohortAction from './SaveCohortAction';
import RequestCohortAccessAction from './RequestCohortAccessAction';
import { cohortPermissions } from '../constants';
import ShareCohortAction from '../sharing/containers/ShareCohortAction';
import { cohortPerms } from '../sharing/state/state';
import { canEditCohort } from '../helper';
import cohortStatus from '../status/helper';

const accessControl = (
  role,
  perms,
  isEditing = false,
  disableEdit = true,
  isNew = false,
  cohortAccess = {},
) => {
  const { cohorts } = role.permissions;
  const { query_access: queryAccess } = cohortAccess;
  return (
    {
      allowEdit: canEditCohort({ permissions: perms, isNew }, role),
      allowDelete: cohorts.destroy && perms[cohortPermissions.DESTROY],
      allowAudit: cohorts.audit && perms[cohortPermissions.AUDIT],
      disableEdit: disableEdit || !queryAccess,
      disableDelete: disableEdit, // TODO: allow for datasetAccess: false??
      disableRequestAccess: disableEdit || isEditing || !queryAccess,
      disableAudit: disableEdit,
      allowSecure: cohorts.secure && perms[cohortPermissions.SECURITY],
      allowShare: cohorts.share && (perms[cohortPermissions.INVITE_MEMBER]
            || perms[cohortPermissions.REMOVE_MEMBER]),
      disableShare: queryAccess === false,
    });
};

const withoutItem = (role) => ({
  exists: false,
  url: '',
  workflowKey: '',
  ...readStates,
  ...accessControl(role, cohortPerms(false)),
});

const withItem = (item, role) => {
  const perms = item.permissions;
  const disableEdit = !!item.isDirty || item.readStatus === states.PENDING;
  const {
    isEditing, isNew, access,
  } = item;

  return (
    {
      exists: true,
      url: cohortUrl(item, '#'),
      workflowKey: item.settings.workflow_key,
      validationErrors: item.validationErrors,
      readStatus: item.readStatus,
      isNew: item.isNew,
      isEditing: item.isEditing,
      ...cohortStatus(role, item),
      ...accessControl(role, perms, isEditing, disableEdit, isNew, access),
    }
  );
};

const CohortActions = ({
  code,
  category,
  workflowKey,
  accessRequestAllowed,
  accessRequestDisabled,
  accessRequestSupported,
  allowEdit,
  disableEdit,
  showEdit,
  isEditing,
  isNew,
  allowDelete,
  allowAudit,
  url,
  showDelete,
  disableDelete,
  disableAudit,
  allowSecure,
  allowShare,
  disableShare,
}) => {
  const intl = useIntl();
  return (
    <>
      {allowEdit && !isEditing && (
        <EditAction onClick={() => showEdit(category, code, intl)} disabled={disableEdit} />
      )}
      {allowEdit && isEditing && !isNew && (<CancelCohortAction code={code} category={category} />)}
      {allowEdit && (<SaveCohortAction code={code} category={category} isEditing={isEditing} />)}
      {accessRequestAllowed && accessRequestSupported && (
        <RequestCohortAccessAction
          code={code}
          workflowKey={workflowKey}
          disabled={accessRequestDisabled}
        />
      )}
      {(!isNew && (allowShare || allowSecure)) && (
        <ShareCohortAction
          code={code}
          category={category}
          secureOnly={allowSecure && !allowShare}
          disabled={disableShare}
        />
      )}
      {allowAudit && !isNew && (<AuditAction baseUrl={url} disabled={disableAudit} />)}
      {allowDelete && !isNew && (
        <DeleteActionMenu onClick={() => showDelete(category, code)} disabled={disableDelete} />
      )}
    </>
  );
};

const mapStateToProps = ({
  auth: { role },
  cohorts: { cohorts },
}, { category, code }) => {
  const item = findResult(cohorts, category, code);
  return item ? withItem(item, role) : withoutItem(role);
};

const mapDispatchToProps = {
  showDelete: confirmCohortDeletion,
  showEdit: editCohort,
};

export default connect(mapStateToProps, mapDispatchToProps)(CohortActions);
